import { type AxisValue } from '~/components/Fields/AxisTripletValue';
import { formatMetric } from '~/containers/workflows/verify/segmetricsColumns';
import { NOT_AVAILABLE } from '~/utils/measurementFormat';
import {
    formatDisplayIntensity,
    imageIntensityUnit,
    toDisplayIntensity,
} from '~/domain/dicom/intensityUnits';
import { RoiStatistics } from '~/engine/util/algorithms/sdf/ContourStatistics';
import { RoiIntensityStats } from '~/engine/data/structure/RegionOfInterest';
import { SUMMARY_STAT_LABEL_IDS } from '~/utils/summaryStatLabels';
import { type AxisKey } from '~/utils/units';

const MM2_PER_CM2 = 100;
const MM_PER_CM = 10;

/**
 * A row of the statistics list. Scalar rows carry one formatted value with its
 * unit appended (`"12.57 cm²"`); axis rows carry an X/Y/Z component each plus
 * one shared `unit` after the triplet. The unit sits on the value, never the
 * label, so every row reads the same way (docs/ui_specs.md).
 */
export type StatRow =
    | {
          readonly kind: 'scalar';
          readonly labelId: string;
          readonly value: string;
      }
    | {
          readonly kind: 'axes';
          readonly labelId: string;
          readonly axes: readonly AxisValue[];
          /** Display unit shown once after the triplet (e.g. `"cm"`). */
          readonly unit: string;
      };

const AXES: readonly AxisKey[] = ['x', 'y', 'z'];

/** The shape rows' labels, in their order. */
const SHAPE_LABEL_IDS = {
    surfaceArea: 'roi.stats.surfaceArea',
    sphericity: 'roi.stats.sphericity',
    centerOfMass: 'roi.stats.centerOfMass',
    boundingBox: 'roi.stats.boundingBox',
} as const;

/** The intensity rows' statistics, in their order. */
const INTENSITY_KEYS = ['mean', 'std', 'min', 'max'] as const;

/** A statistics row before its value is known. */
export interface StatRowPlaceholder {
    readonly labelId: string;
    /** About the width of the row's usual value, in digit widths. */
    readonly valueWidthCh: number;
}

/**
 * Every statistics row before its value is known, in order: shape rows,
 * then intensity rows. Each holds about the width of a usual value, so a
 * placeholder is about as wide as the value that replaces it. Digits are
 * the widest characters of a value, so a value is fewer digit widths wide
 * than it has characters.
 */
export const STAT_ROW_PLACEHOLDERS: readonly StatRowPlaceholder[] = [
    // "1296.98 cm²"
    { labelId: SHAPE_LABEL_IDS.surfaceArea, valueWidthCh: 8 },
    // "0.850"
    { labelId: SHAPE_LABEL_IDS.sphericity, valueWidthCh: 4 },
    // "X 0.14  Y 2.87  Z -3.97 cm"
    { labelId: SHAPE_LABEL_IDS.centerOfMass, valueWidthCh: 24 },
    { labelId: SHAPE_LABEL_IDS.boundingBox, valueWidthCh: 24 },
    // "109.2 HU", "433.3 HU", "-1024.0 HU", "3071.0 HU"
    ...INTENSITY_KEYS.map((key) => ({
        labelId: SUMMARY_STAT_LABEL_IDS[key],
        valueWidthCh: { mean: 6, std: 6, min: 8, max: 7 }[key],
    })),
];

/**
 * Format three mm values as cm (2 dp), one labeled component per patient axis.
 * Returns null when any component is missing or non-finite, so the caller can
 * fall back to a single "not available" cell rather than a partial triplet.
 */
function formatAxisTripletCm(
    values: readonly number[] | null
): AxisValue[] | null {
    if (!values) {
        return null;
    }
    const formatted = values.map((mm) => formatMetric(mm, 2, MM_PER_CM));
    if (formatted.some((part) => part === null)) {
        return null;
    }
    return AXES.map((axis, i) => ({ axis, value: formatted[i] as string }));
}

function scalarRow(labelId: string, value: string | null): StatRow {
    return { kind: 'scalar', labelId, value: value ?? NOT_AVAILABLE };
}

/** Append a unit to a formatted value, or pass a "not available" cell through. */
function withUnit(value: string | null, unit: string): string | null {
    return value === null ? null : `${value} ${unit}`;
}

/** An axis row, or a "not available" scalar cell when the triplet is null. */
function axisRow(
    labelId: string,
    axes: AxisValue[] | null,
    unit: string
): StatRow {
    return axes
        ? { kind: 'axes', labelId, axes, unit }
        : { kind: 'scalar', labelId, value: NOT_AVAILABLE };
}

/**
 * Maps engine SDF statistics to display rows, converting mm to cm at the UI
 * boundary. No volume row: the Properties section carries it.
 */
export function roiStatRows(stats: RoiStatistics): StatRow[] {
    const com = stats.centerOfMassMm;
    const bbox = stats.boundingBox;
    return [
        scalarRow(
            SHAPE_LABEL_IDS.surfaceArea,
            withUnit(formatMetric(stats.surfaceAreaMm2, 2, MM2_PER_CM2), 'cm²')
        ),
        scalarRow(
            SHAPE_LABEL_IDS.sphericity,
            formatMetric(stats.sphericity, 3)
        ),
        axisRow(
            SHAPE_LABEL_IDS.centerOfMass,
            formatAxisTripletCm(com ? [com[0], com[1], com[2]] : null),
            'cm'
        ),
        axisRow(
            SHAPE_LABEL_IDS.boundingBox,
            formatAxisTripletCm(
                bbox ? AXES.map((_, i) => bbox.max[i] - bbox.min[i]) : null
            ),
            'cm'
        ),
    ];
}

/**
 * Image intensity under an ROI mask as display rows. One unit for all four
 * statistics, read once from the header, formatted through the shared
 * intensity formatter so two readouts of one image cannot disagree.
 */
export function roiIntensityStatRows({
    statistics,
    image,
}: RoiIntensityStats): StatRow[] {
    const unit = imageIntensityUnit(image);
    const format = (value: number): string =>
        formatDisplayIntensity(toDisplayIntensity(value, image), unit);
    return INTENSITY_KEYS.map((key) => ({
        kind: 'scalar',
        labelId: SUMMARY_STAT_LABEL_IDS[key],
        value: format(statistics[key]),
    }));
}
